import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export function ImproveArticleDialog({
  open,
  onOpenChange,
  pending,
  onImprove,
  initialFeedback = '',
  title = 'Improve article',
  description = 'Create a revised draft from this saved version. With no added direction, Mora will address the current critique while preserving the brief, sources and brand voice.',
  submitLabel = 'Improve article',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onImprove: (feedback: string) => Promise<unknown>;
  /** Seeds the box each time the dialog opens — a retry keeps what was already written. */
  initialFeedback?: string;
  title?: string;
  description?: string;
  submitLabel?: string;
}) {
  const [feedback, setFeedback] = useState(initialFeedback);
  useEffect(() => {
    if (open) setFeedback(initialFeedback);
  }, [open, initialFeedback]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!pending) onOpenChange(next);
      }}
    >
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="article-improvement-feedback">
            What should improve? <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Textarea
            id="article-improvement-feedback"
            value={feedback}
            disabled={pending}
            maxLength={4000}
            maxRows={8}
            onChange={(event) => setFeedback(event.target.value)}
            placeholder="For example, make the opening more specific and clarify which evidence supports the recommendation."
          />
        </div>
        <DialogFooter>
          <Button variant="flat" disabled={pending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="neutral"
            disabled={pending || feedback.length > 4000}
            onClick={() =>
              void onImprove(feedback)
                .then(() => {
                  setFeedback('');
                  onOpenChange(false);
                })
                .catch((error) =>
                  toast.error(
                    error instanceof Error ? error.message : 'Could not start the improvement.',
                  ),
                )
            }
          >
            {pending ? 'Starting…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
